import LegalDocument, { LegalSection } from '../components/legal/LegalDocument';

export default function Terms() {
  return (
    <LegalDocument title="Terms & Conditions" lastUpdated="24 July 2026">
      <LegalSection title="1. About these terms">
        <p>
          These Terms &amp; Conditions (&quot;Terms&quot;) govern your use of the PropScope
          website and services at propscope.co.uk (the &quot;Service&quot;), operated by
          Geekybee Limited (&quot;we&quot;, &quot;us&quot;, company number and registered
          office as shown at Companies House), trading as PropScope.
        </p>
        <p>
          By creating an account or using the Service, you agree to these Terms. If you do
          not agree, do not use the Service.
        </p>
      </LegalSection>

      <LegalSection title="2. What PropScope provides">
        <p>
          PropScope is a UK property investment intelligence platform. We aggregate and
          present property listings, Land Registry and other open or licensed data, area
          statistics, scoring, and analytical tools to help users research potential
          investments.
        </p>
        <p>
          The Service is an information tool only. We do not provide financial, legal,
          tax, or investment advice. You are solely responsible for your investment
          decisions. Valuations, scores, AI-generated commentary, and yield estimates are
          indicative and may be incomplete or inaccurate.
        </p>
      </LegalSection>

      <LegalSection title="3. Accounts">
        <p>
          You must provide accurate registration details and keep your password secure.
          You are responsible for activity under your account. You must be at least 18
          years old and have authority to enter these Terms.
        </p>
        <p>
          We may suspend or close accounts that breach these Terms, abuse the Service, or
          pose a security risk.
        </p>
      </LegalSection>

      <LegalSection title="4. Free access and paid subscriptions">
        <p>
          <strong className="text-slate-300">Free account:</strong> lets you browse
          property listings and area reports with limited functionality (filters, scores,
          and premium analytics may be restricted).
        </p>
        <p>
          <strong className="text-slate-300">Basic and Pro subscriptions:</strong> unlock
          additional features as described on the plan picker and your account page.
          Subscriptions are billed through Stripe. Prices, features, and plan names may
          change; we will give reasonable notice of material changes where required.
        </p>
      </LegalSection>

      <LegalSection title="5. Free trial and payment">
        <p>
          New Basic or Pro subscribers may receive a <strong className="text-slate-300">7-day
          free trial</strong>. A valid payment card is required at signup. You will not be
          charged until the trial ends unless you cancel before then.
        </p>
        <p>
          After the trial, your subscription renews automatically at the advertised monthly
          rate until you cancel via the Stripe customer portal (linked from your Account
          page) or as otherwise described at checkout.
        </p>
        <p>
          Failed payments may result in restricted access. Refunds are at our discretion
          except where required by law.
        </p>
      </LegalSection>

      <LegalSection title="6. Acceptable use">
        <p>You agree not to:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Scrape, bulk-download, or resell Service data without permission</li>
          <li>Circumvent access controls or share login credentials</li>
          <li>Use the Service unlawfully or in a way that harms other users or our systems</li>
          <li>Misrepresent affiliation with PropScope or Geekybee Limited</li>
        </ul>
      </LegalSection>

      <LegalSection title="7. Intellectual property and data">
        <p>
          PropScope branding, software, and original content are owned by or licensed to
          Geekybee Limited. Third-party data (including Land Registry Price Paid Data)
          remains subject to the respective licences. You must comply with any attribution
          requirements shown in the Service.
        </p>
        <p>
          You retain rights in content you upload (e.g. listings you submit as an auction
          house or agent). You grant us a licence to host, display, and process that
          content to operate the Service.
        </p>
      </LegalSection>

      <LegalSection title="8. Availability and changes">
        <p>
          We aim to keep the Service available but do not guarantee uninterrupted access.
          Maintenance, data-source outages, or third-party failures may affect availability.
          We may modify or withdraw features with reasonable notice where practicable.
        </p>
      </LegalSection>

      <LegalSection title="9. Limitation of liability">
        <p>
          To the fullest extent permitted by law, we exclude liability for indirect,
          consequential, or economic loss arising from use of the Service. Our total
          liability to you for any claim relating to the Service in any 12-month period
          is limited to the fees you paid us in that period (or £100 if you use only the
          free tier).
        </p>
        <p>
          Nothing in these Terms limits liability for death or personal injury caused by
          negligence, fraud, or any liability that cannot be excluded under English law.
        </p>
      </LegalSection>

      <LegalSection title="10. Privacy">
        <p>
          Our <a href="/privacy" className="text-emerald-400 hover:underline">Privacy Policy</a>{' '}
          explains how we handle personal data.
        </p>
      </LegalSection>

      <LegalSection title="11. Governing law">
        <p>
          These Terms are governed by the laws of England and Wales. The courts of England
          and Wales have exclusive jurisdiction, except where mandatory consumer protection
          laws in your country of residence give you additional rights.
        </p>
      </LegalSection>

      <LegalSection title="12. Contact">
        <p>
          Questions about these Terms:{' '}
          <a href="mailto:info@geekybee.net" className="text-emerald-400 hover:underline">
            info@geekybee.net
          </a>
        </p>
      </LegalSection>
    </LegalDocument>
  );
}
